import {
  collection,
  doc,
  getDoc,
  getDocs,
  getFirestore,
  limit,
  orderBy,
  query,
  setDoc,
  where,
} from "@firebase/firestore";
import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import { PlatformConnection, PlatformType } from "../models/Platform";
import { AnyOverlay, ImageOverlay, Post, SvgOverlay, TextOverlay } from "../models/Post";
import { auth, db, getSignedUrl } from "../services/Firebase";
import { postConverter } from "../services/converters";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Compute connection status based on token age
 * - LinkedIn & TikTok: Refresh tokens expire after 1 year
 * - Facebook: Honor Meta's actual Page-token/data-access expiry
 * - Instagram: Tokens can be refreshed indefinitely
 */
export function computeConnectionStatus(
  connection: PlatformConnection
): PlatformConnection {
  const now = Date.now();
  const platform = connection.platform;

  // If a terminal status was already written server-side (e.g. an auth failure
  // during publish flipped it to "expired", or the user revoked), trust it —
  // the token-age heuristic below must not override or downgrade it. Personal
  // LinkedIn dies at ~60 days (no refresh token), long before the 1-year age
  // threshold, so the stored flag is the only correct signal there.
  if (connection.status === "revoked" || connection.status === "expired") {
    return connection;
  }

  const fifteenDays = 15 * 24 * 60 * 60 * 1000;

  // Preferred, platform-agnostic signal: the real refresh-token expiry, captured
  // from the provider on connect/refresh (LinkedIn/TikTok). It's accurate AND it
  // resets on reconnect — unlike the createdAt age heuristics below, which never
  // reset and would falsely nag a freshly-reconnected connection (and can't be
  // cleared by reconnecting). Any connection carrying refreshExpiresAt uses it.
  if (connection.refreshExpiresAt) {
    if (now >= connection.refreshExpiresAt) {
      return { ...connection, status: "expired" };
    }
    if (now >= connection.refreshExpiresAt - fifteenDays) {
      return { ...connection, status: "expiring_soon" };
    }
    return { ...connection, status: connection.status || "active" };
  }

  // Fallback for connections saved before refreshExpiresAt was captured (healed
  // on their next reconnect): legacy per-platform token-age heuristics.
  // LinkedIn & TikTok: Check refresh token age (1 year expiration)
  if (platform === "linkedin" || platform === "tiktok") {
    const tokenAge = now - connection.createdAt;
    const oneYear = 365 * 24 * 60 * 60 * 1000;

    if (tokenAge > oneYear) {
      return { ...connection, status: "expired" };
    } else if (tokenAge > oneYear - fifteenDays) {
      return { ...connection, status: "expiring_soon" };
    }
  }

  // Facebook Page tokens derived from a long-lived User token commonly have
  // no fixed expiry (stored as null). Only warn when Meta reports a positive,
  // Page-token-specific expiresAt value.
  if (platform === "facebook") {
    const tenDays = 10 * 24 * 60 * 60 * 1000;

    if (
      typeof connection.expiresAt === "number" &&
      connection.expiresAt > 0
    ) {
      if (now >= connection.expiresAt) {
        return { ...connection, status: "expired" };
      }
      if (now + tenDays >= connection.expiresAt) {
        return { ...connection, status: "expiring_soon" };
      }
      return { ...connection, status: connection.status || "active" };
    }
    return { ...connection, status: "active" };
  }

  // Pinterest: Access tokens can be refreshed, but surface expiry warnings
  // based on the provider's expiresAt timestamp.
  if (platform === "pinterest" && connection.expiresAt) {
    const threeDays = 3 * 24 * 60 * 60 * 1000;
    if (now >= connection.expiresAt) {
      return { ...connection, status: "expired" };
    } else if (now + threeDays >= connection.expiresAt) {
      return { ...connection, status: "expiring_soon" };
    }
  }

  // Instagram & YouTube: Access tokens can be refreshed, no expiry concerns
  // Just keep the current status or set to active
  return { ...connection, status: connection.status || "active" };
}

export async function getAllValidConnectionsForUser(userId: string, signal?: AbortSignal) {
  const currentUser = auth.currentUser;
  if (!currentUser) throw new Error("Sign in to load connected accounts.");
  const token = await currentUser.getIdToken();
  const response = await fetch(`${process.env.REACT_APP_API_BASE}/connections`, {
    signal,
    headers: {
      Authorization: `Bearer ${token}`,
      "x-workspace-id": userId,
    },
  });
  if (response.status === 404) {
    // Client-first rollout compatibility. The final rules leave this bridge
    // available only to legacy UID owners, never teammates/opaque workspaces.
    const connectionsRef = collection(db, "platform_connections");
    const legacySnapshot = await getDocs(
      query(
        connectionsRef,
        where("userId", "==", userId),
        where("status", "!=", "revoked"),
      ),
    );
    return legacySnapshot.docs.map((connectionDoc) =>
      computeConnectionStatus({
        id: connectionDoc.id,
        ...connectionDoc.data(),
      } as PlatformConnection),
    );
  }
  if (!response.ok) {
    throw new Error(`Failed to load connected accounts (${response.status}).`);
  }
  const connections = (await response.json()) as PlatformConnection[];
  return connections.map(computeConnectionStatus);
}

export const groupConnectionsByPlatform = (
  connections: PlatformConnection[]
) => {
  return connections.reduce(
    (groups, connection) => {
      const platform = connection.platform;
      if (!groups[platform]) {
        groups[platform] = [];
      }
      groups[platform].push(connection);
      return groups;
    },
    {} as Record<PlatformType, PlatformConnection[]>
  );
};

export const getPostForUser = async (
  id: string,
  uid: string
): Promise<Post | null> => {
  const draftRef = doc(db, "posts", id).withConverter(postConverter);
  const draftSnap = await getDoc(draftRef);

  if (draftSnap.exists() && draftSnap.data().userId === uid) {
    return draftSnap.data();
  }
  return null;
};

export const getMostRecentDraftForUser = async (
  uid: string
): Promise<Post | null> => {
  const draftsRef = collection(db, "posts").withConverter(postConverter);
  const q = query(
    draftsRef,
    where("userId", "==", uid),
    where("status", "==", "draft"),
    orderBy("updatedAt", "desc"),
    limit(1)
  );

  const querySnapshot = await getDocs(q);
  const draftDoc = querySnapshot.docs[0];

  if (draftDoc) {
    return draftDoc.data();
  }
  return null;
};

export const createNewDraftForWorkspace = async (
  workspaceId: string,
  actorUserId: string
): Promise<string> => {
  const newDraftRef = doc(collection(db, "posts")).withConverter(postConverter);
  const timestamp = new Date().getTime();

  const newDraft: Partial<Post> = {
    id: newDraftRef.id,
    userId: workspaceId,
    workspaceId,
    createdByUserId: actorUserId,
    status: "draft",
    generationSource: null,
    mediaItems: [],
    connections: {},
    createdAt: timestamp,
    updatedAt: timestamp,
  };

  await setDoc(newDraftRef, newDraft);
  return newDraftRef.id;
};

export const formatDate = (
  timestamp: number | undefined,
  format: "datetime-local" | "display" = "display"
) => {
  if (!timestamp || timestamp === 0) return "";
  const date = new Date(timestamp);

  if (format === "datetime-local") {
    // Format for datetime-local input (YYYY-MM-DDThh:mm)
    return date
      .toLocaleString("sv-SE", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      })
      .replace(" ", "T");
  }

  // Default human-readable format
  return date.toLocaleString(navigator.language, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export const loadMediaUrls = async (
  posts: Post[]
): Promise<Record<string, string>> => {
  const seen = new Set<string>();
  const urlPromises: Promise<[string, string]>[] = [];

  const addPath = (path: string) => {
    if (!path || seen.has(path)) return;
    seen.add(path);
    urlPromises.push(
      getSignedUrl(path).then((url) => [path, url] as [string, string])
    );
  };

  posts.forEach((post) => {
    post.mediaItems.forEach((mediaItem) => {
      if (mediaItem === null || mediaItem === undefined) return;

      // Main media item
      addPath(mediaItem.path);

      // Thumbnail (video)
      if (mediaItem.type === "video" && mediaItem.metadata.thumbnailPath) {
        addPath(mediaItem.metadata.thumbnailPath);
      }

      // Overlay image filePaths
      if (mediaItem.metadata.overlays) {
        for (const overlay of mediaItem.metadata.overlays) {
          if (overlay.type === "image" && "filePath" in overlay && overlay.filePath) {
            addPath(overlay.filePath);
          }
        }
      }
    });
  });

  const settled = await Promise.allSettled(urlPromises);
  const result: Record<string, string> = {};
  for (const entry of settled) {
    if (entry.status === "fulfilled") {
      const [path, url] = entry.value;
      result[path] = url;
    }
  }
  return result;
};

/**
 * Converts HEIC/HEIF images to PNG format
 * @param file The file to convert
 * @returns A Promise that resolves to the converted file or the original file if no conversion is needed
 */
export async function convertHeicToPng(file: File): Promise<File> {
  // Check if the file is a HEIC/HEIF image by extension or type
  const isHeic =
    file.name.toLowerCase().endsWith(".heic") ||
    file.name.toLowerCase().endsWith(".heif") ||
    file.type.toLowerCase() === "image/heic" ||
    file.type.toLowerCase() === "image/heif";

  if (!isHeic) {
    return file; // Return the original file if it's not HEIC/HEIF
  }

  try {
    // Dynamically import heic2any to avoid issues with SSR
    const heic2any = (await import("heic2any")).default;

    // Convert the HEIC file to PNG
    const pngBlob = (await heic2any({
      blob: file,
      toType: "image/png",
    })) as Blob;

    // Create a new file with the converted PNG data
    const newFileName = file.name.replace(/\.(heic|heif)$/i, ".png");
    const convertedFile = new File([pngBlob], newFileName, {
      type: "image/png",
    });

    return convertedFile;
  } catch (error) {
    console.error("Error converting HEIC image:", error);
    return file; // Return the original file if conversion fails
  }
}

export function isTextOverlay(overlay: AnyOverlay): overlay is TextOverlay {
  // Assumes that if `type` is missing, it's a legacy TextOverlay
  return !overlay.type || overlay.type === "text";
}

export function isImageOverlay(overlay: AnyOverlay): overlay is ImageOverlay {
  return overlay.type === "image";
}

export function isSvgOverlay(overlay: AnyOverlay): overlay is SvgOverlay {
  return overlay.type === "svg";
}
